import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  Photo
} from 'tabler-icons-react';
import {
  Anchor,
  Button,
  Image,
  Skeleton,
  Tabs,
  Textarea,
  TextInput
} from '@mantine/core';
import PropTypes from 'prop-types';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { useAdminBackPath } from '../../../helpers/adminHelper';
import { triggerNotification } from '../../../helpers/notification';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import ActionableIcon from '../../common/ActionableIcon';
import AppCard from '../../common/AppCard';
import AppText from '../../common/AppText';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import EditProductCard from '../products/EditProductCard';
import StoreCreationStoreSelectModal from '../wizard/StoreCreationStoreSelectModal';
import SettingSelector from '../../common/SettingSelector';
import ConfirmModal from '../../common/ConfirmModal';
import AdminStockContentStoreProductsList from './AdminStockContentStoreProductsList';
import AdminStockContentAssignedStoreList from './AdminStockContentAssignedStoreList';

const VIEW_TABS = [
  { value: 'products', label: 'Products' },
  { value: 'stores', label: 'Assigned stores' }
];

const AdminStockContentDetailsView = ({ duplicate }) => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const {
    isLargeMobileOrSmaller,
    isTabletOrSmaller,
    isLaptopOrSmaller
  } = useMediaQueryIndex();
  const { pkEcomStockContent } = useParams();
  const {
    state,
    fetchEcomStockContent,
    createEcomStockContent,
    updateEcomStockContent,
    deleteEcomStockContent
  } = useContext(AdminContext);
  const { backPath } = useAdminBackPath(
    duplicate
      ? `/admin/stock-content/${pkEcomStockContent}`
      : '/admin/stock-content'
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [viewState, setViewState] = useState({ activeTab: VIEW_TABS[0].value });
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(
    {
      title: '',
      description: '',
      ecomStore: null
    },
    { confirmDiscard: true, containerWidth: 1100 }
  );

  const ecomStockContent =
    state.ecomStockContent.value?.pkEcomStockContent.toString() ===
    pkEcomStockContent
      ? state.ecomStockContent.value
      : null;

  const hasAlreadyFetched =
    hasFetched.current ||
    ecomStockContent?.pkEcomStockContent.toString() === pkEcomStockContent;
  const loading = pkEcomStockContent
    ? !hasInitialized || !hasAlreadyFetched || state.ecomStockContent.loading
    : !hasInitialized;

  useEffect(() => {
    if (pkEcomStockContent) {
      fetchEcomStockContent(pkEcomStockContent, null, triggerNotification);
    }
    hasFetched.current = true;
  }, [pkEcomStockContent]);

  useEffect(() => {
    if (
      !state.ecomStockContent.loading &&
      (!pkEcomStockContent || ecomStockContent)
    ) {
      resetFormState({
        title: ecomStockContent?.title ?? '',
        description: ecomStockContent?.description ?? '',
        ecomStore: ecomStockContent?.ecomStore
          ? {
              pkEcomStore: ecomStockContent.ecomStore.pkEcomStore,
              name: ecomStockContent.ecomStore.name,
              domain: ecomStockContent.ecomStore.domain,
              logoImageUrl: ecomStockContent.ecomStore.logoImageUrl,
              urlHandle: ecomStockContent.ecomStore.urlHandle
            }
          : null
      });
    }
  }, [ecomStockContent, state.ecomStockContent.loading, duplicate]);

  const onSaveStockContent = () => {
    submitFormState((formData, onErrorCallback) => {
      if (ecomStockContent && !duplicate) {
        updateEcomStockContent(
          {
            ecomStockContent: [
              {
                pkEcomStockContent: ecomStockContent.pkEcomStockContent,
                title: formState.title,
                description: formState.description,
                fkEcomStore: formState.ecomStore
                  ? formState.ecomStore.pkEcomStore
                  : null
              }
            ],
            selectFirstItem: true
          },
          () => {
            setFormState({ ...formState, isEditing: false, loading: false });
            triggerNotification('Stock content updated!', 'success');
          },
          (e) => {
            setFormState({ ...formState, loading: false });
            triggerNotification(e);
          }
        );
      }
      else {
        createEcomStockContent(
          [
            {
              title: formState.title,
              description: formState.description,
              fkEcomStore: formState.ecomStore
                ? formState.ecomStore.pkEcomStore
                : null
            }
          ],
          (data) => {
            triggerNotification('Stock content created.', 'success');
            navigate(`/admin/stock-content/${data[0].pkEcomStockContent}`);
          },
          (e) => {
            setFormState({ ...formState, loading: false });
            triggerNotification(e);
          }
        );
      }
    });
  };

  return !loading && (!pkEcomStockContent || hasInitialized) ? (
    <>
      <AppStack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSaveStockContent();
        }}
        p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
        style={{
          flex: 1,
          margin: 'auto',
          width: '100%',
          maxWidth: 1100,
          paddingTop: 16,
          gap: 16
        }}
      >
        <AppFlexbox
          p={{ base: '0px 8px', sm: 0 }}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <ActionableIcon
              color="dark"
              component={Link}
              radius="md"
              to={backPath}
              variant="subtle"
            >
              <ArrowLeft />
            </ActionableIcon>
            <AppText style={{ fontSize: 18, fontWeight: 700 }}>
              {ecomStockContent?.title ?? 'Create stock content'}
            </AppText>
          </AppFlexbox>

          {!!ecomStockContent && !duplicate && (
            <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
              <Button
                color="dark"
                component={Link}
                radius="md"
                size="xs"
                to={`/admin/stock-content/${ecomStockContent.pkEcomStockContent}/duplicate`}
                type="button"
                variant="light"
              >
                Duplicate
              </Button>
            </AppFlexbox>
          )}
        </AppFlexbox>

        {duplicate ? (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="#eca70a" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Duplicate this stock content by changing the <b>Title</b> and
                clicking save.
              </AppText>
            </AppFlexbox>
          </AppCard>
        ) : (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 8 }}
            withBorder
          >
            <AppFlexbox style={{ gap: 8 }}>
              <AppStack>
                <AlertCircle color="dodgerblue" style={{ size: 18 }} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>
                Create stock content by selecting a template store whose
                webstore, products, and collections will be copied by other
                stores.
              </AppText>
            </AppFlexbox>
          </AppCard>
        )}

        <AppFlexbox
          style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
        >
          <AppStack
            style={{
              flex: 1,
              gap: 16
            }}
          >
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 16 }}
              withBorder
            >
              <AppStack>
                <TextInput
                  disabled={isSubmitting}
                  label="Title"
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      title: e.currentTarget.value,
                      hasUnsavedChanges: true
                    })
                  }
                  required
                  value={formState.title}
                />

                <Textarea
                  autosize
                  disabled={isSubmitting}
                  label="Description"
                  minRows={4}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      description: e.currentTarget.value,
                      hasUnsavedChanges: true
                    })
                  }
                  value={formState.description}
                />
              </AppStack>
            </AppCard>

            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 0 }}>
                <Tabs
                  onChange={(activeTab) =>
                    setViewState({ ...viewState, activeTab })
                  }
                  value={viewState.activeTab}
                >
                  <Tabs.List>
                    {VIEW_TABS.map((tab) => (
                      <Tabs.Tab key={tab.value} color="dark" value={tab.value}>
                        <AppText
                          style={{
                            fontSize: 14,
                            fontWeight:
                              viewState.activeTab === tab.value ? 500 : 'normal'
                          }}
                        >
                          {tab.label}
                        </AppText>
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                </Tabs>

                <AppStack>
                  {viewState.activeTab === 'products' ? (
                    <AdminStockContentStoreProductsList
                      pkEcomStore={formState.ecomStore?.pkEcomStore}
                    />
                  ) : (
                    viewState.activeTab === 'stores' && (
                      <AdminStockContentAssignedStoreList
                        pkEcomStockContent={pkEcomStockContent}
                      />
                    )
                  )}
                </AppStack>
              </AppStack>
            </AppCard>
          </AppStack>

          <AppStack
            style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 350 }}
          >
            <EditProductCard
              contentProps={{ style: { padding: 0 } }}
              title="Store content"
              titleAction={
                formState.ecomStore && (
                  <Anchor
                    component={Link}
                    style={{ fontSize: 14, color: 'dodgerblue' }}
                    target="_blank"
                    to={`/store/${formState.ecomStore.urlHandle}`}
                  >
                    View webstore
                  </Anchor>
                )
              }
            >
              <AppStack style={{ gap: 0 }}>
                <AppStack style={{ padding: '0px 16px 16px 16px' }}>
                  <SettingSelector
                    description={
                      formState.ecomStore
                        ? formState.ecomStore?.domain
                        : 'No store assigned'
                    }
                    descriptionColor={
                      formState.ecomStore?.name ? '#666' : '#c40000'
                    }
                    disabled={formState.loading}
                    leftSection={
                      formState.ecomStore ? (
                        <AppStack
                          style={{
                            width: 40,
                            height: 40,
                            border: formState.ecomStore.logoImageUrl
                              ? 'none'
                              : 'solid 1px #dee2e6',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {formState.ecomStore.logoImageUrl ? (
                            <Image
                              h={40}
                              src={formState.ecomStore.logoImageUrl}
                              w={40}
                            />
                          ) : (
                            <Photo color="#666" size={24} />
                          )}
                        </AppStack>
                      ) : (
                        <AlertCircle color="#c40000" size={32} />
                      )
                    }
                    onClick={() => {
                      onOpenModal('select_store');
                    }}
                    rightSection={
                      <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                        <ChevronRight size={18} />
                      </AppFlexbox>
                    }
                    title={formState.ecomStore?.name || 'Click to select store'}
                    withBorder
                  />
                </AppStack>
              </AppStack>
            </EditProductCard>
          </AppStack>
        </AppFlexbox>

        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{
            justifyContent: 'flex-end',
            flexDirection: isLargeMobileOrSmaller ? 'column-reverse' : 'row'
          }}
        >
          {!!ecomStockContent && !duplicate && (
            <Button
              color="red"
              disabled={isSubmitting}
              onClick={() => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE);
              }}
              radius="md"
              size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
              type="button"
              variant="filled"
            >
              Delete stock content
            </Button>
          )}
          <Button
            color="dark"
            disabled={!hasUnsavedChanges}
            id="save-button"
            loading={isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save
          </Button>
        </AppFlexbox>
      </AppStack>

      {ConfirmDiscardModal}

      <StoreCreationStoreSelectModal
        isOpen={modalState.isOpen && modalState.action === 'select_store'}
        onClose={onCloseModal}
        onSelect={(s) => {
          onCloseModal();
          setFormState({
            ...formState,
            ecomStore: {
              pkEcomStore: s.pkEcomStore,
              name: s.name,
              domain: s.domain,
              logoImageUrl: s.logoImageUrl,
              urlHandle: s.urlHandle
            },
            hasUnsavedChanges: true
          });
        }}
      />

      {ecomStockContent && (
        <ConfirmModal
          confirmActionColor="red"
          confirmActionText="Yes, delete content"
          isLoading={modalState.loading}
          isOpen={
            modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
          }
          message={
            <AppStack style={{ gap: 10 }}>
              <AppText style={{ fontSize: 14 }}>
                Are you sure you want to delete <b>{ecomStockContent.title}</b>?
              </AppText>

              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                This action can’t be undone.
              </AppText>
            </AppStack>
          }
          onCancel={onCloseModal}
          onConfirm={() => {
            onChangeModalLoading(true);
            deleteEcomStockContent(
              [pkEcomStockContent],
              () => {
                triggerNotification('Stock content deleted.', 'success');
                navigate('/admin/stock-content');

                onCloseModal();
              },
              (error) => {
                triggerNotification(error);
                onChangeModalLoading(false);
              }
            );
          }}
          title={`Delete ${modalState.item?.name}?`}
        />
      )}
    </>
  ) : (
    <AppStack
      p={{ base: '8px 0px', sm: '16px 16px 80px 16px' }}
      style={{
        flex: 1,
        margin: 'auto',
        width: '100%',
        maxWidth: 1100,
        paddingTop: 16,
        gap: 16
      }}
    >
      <AppFlexbox
        p={{ base: '0px 8px', sm: 0 }}
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <AppFlexbox style={{ gap: 5 }}>
          <Skeleton height={30} width={200} />
        </AppFlexbox>

        {!!ecomStockContent && !duplicate && (
          <AppFlexbox style={{ gap: 5 }}>
            <Skeleton height={30} width={125} />
          </AppFlexbox>
        )}
      </AppFlexbox>

      <AppFlexbox
        style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
      >
        <AppStack
          style={{
            flex: 1,
            gap: 16
          }}
        >
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>

          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 350 }}
        >
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

AdminStockContentDetailsView.propTypes = { duplicate: PropTypes.bool };

export default AdminStockContentDetailsView;
